/**
 * Satış noktaları (F7-37) için konum yardımcıları; vitrinde tarayıcıda ve sunucuda aynı kurallar. Koordinatlar
 * mikroderece tam sayıdır (enlem × 10⁶): kayan nokta yalnızca mesafe hesabında, sonuç metreye yuvarlanır.
 *
 * "Konumumu kullan": ziyaretçinin konumu sunucuya gönderilmez; mesafe bu işlevlerle tarayıcıda hesaplanır.
 */

export interface GeoPoint {
  /** Enlem × 10⁶ (ör. 41.008200 → 41008200). */
  latE6: number;
  /** Boylam × 10⁶. */
  lngE6: number;
}

const EARTH_RADIUS_M = 6_371_008.8;
const rad = (e6: number) => (e6 / 1e6) * (Math.PI / 180);

/** Türkiye ve yakın çevresi (kıyı ve adalar dahil); bunun dışındaki pin yanlış girilmiş sayılır. */
export const TURKEY_BOUNDS = {
  minLatE6: 35_500_000,
  maxLatE6: 42_500_000,
  minLngE6: 25_500_000,
  maxLngE6: 45_000_000,
};

export function isValidPoint(p: Partial<GeoPoint> | null | undefined): p is GeoPoint {
  return (
    !!p &&
    Number.isInteger(p.latE6) &&
    Number.isInteger(p.lngE6) &&
    p.latE6! >= -90_000_000 &&
    p.latE6! <= 90_000_000 &&
    p.lngE6! >= -180_000_000 &&
    p.lngE6! <= 180_000_000
  );
}

export function inTurkey(p: GeoPoint): boolean {
  const b = TURKEY_BOUNDS;
  return p.latE6 >= b.minLatE6 && p.latE6 <= b.maxLatE6 && p.lngE6 >= b.minLngE6 && p.lngE6 <= b.maxLngE6;
}

/** İki nokta arasındaki büyük daire uzaklığı (metre, tam sayı). */
export function distanceMeters(a: GeoPoint, b: GeoPoint): number {
  const dLat = rad(b.latE6 - a.latE6);
  const dLng = rad(b.lngE6 - a.lngE6);
  const h =
    Math.sin(dLat / 2) ** 2 + Math.cos(rad(a.latE6)) * Math.cos(rad(b.latE6)) * Math.sin(dLng / 2) ** 2;
  return Math.round(2 * EARTH_RADIUS_M * Math.asin(Math.min(1, Math.sqrt(h))));
}

/**
 * Ziyaretçiye göre sıralar: konumu (pin) olanlar yakından uzağa, konumu olmayanlar sonda (kendi sıralarıyla).
 * Eşit mesafede özgün sıra korunur.
 */
export function sortByDistance<T extends { point: GeoPoint | null }>(
  items: readonly T[],
  from: GeoPoint,
): Array<T & { distanceM: number | null }> {
  return items
    .map((it, i) => ({ it, i, d: it.point ? distanceMeters(from, it.point) : null }))
    .sort((x, y) => {
      if (x.d === null || y.d === null)
        return x.d === null && y.d === null ? x.i - y.i : x.d === null ? 1 : -1;
      return x.d - y.d || x.i - y.i;
    })
    .map(({ it, d }) => ({ ...it, distanceM: d }));
}

/** Mikroderece → ondalık metin (6 basamak; adreslerde ve bağlantılarda). */
export const e6ToText = (e6: number) => (e6 / 1e6).toFixed(6);

/**
 * Google Haritalar yol tarifi bağlantısı (ziyaretçi tıklarsa Google'a gider; sayfa açılırken hiçbir veri
 * gönderilmez). Pin yoksa adres metniyle aranır.
 */
export function directionsUrl(target: { point: GeoPoint | null; address: string }): string {
  const dest = target.point
    ? `${e6ToText(target.point.latE6)},${e6ToText(target.point.lngE6)}`
    : target.address;
  return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(dest)}`;
}

/**
 * Panelde yapıştırılan konumu okur: "41.0082, 28.9784", "41,0082 28,9784" (virgüllü ondalık) veya Google Haritalar
 * bağlantısı (`@41.0082,28.9784,17z`, `?q=41.0082,28.9784`, `!3d41.0082!4d28.9784`). Metin yerelde çözülür.
 */
export function parseCoordinates(raw: string): GeoPoint | null {
  const s = raw.trim();
  if (!s) return null;
  const pick = (lat: string, lng: string): GeoPoint | null => {
    const la = Number(lat.replace(',', '.'));
    const lo = Number(lng.replace(',', '.'));
    if (!Number.isFinite(la) || !Number.isFinite(lo)) return null;
    const p = { latE6: Math.round(la * 1e6), lngE6: Math.round(lo * 1e6) };
    return isValidPoint(p) ? p : null;
  };
  const num = String.raw`-?\d{1,3}(?:\.\d+)?`;
  const patterns = [
    new RegExp(String.raw`!3d(${num})!4d(${num})`),
    new RegExp(String.raw`@(${num}),(${num})`),
    new RegExp(String.raw`[?&](?:q|query|ll|destination)=(${num}),\s*(${num})`),
    new RegExp(String.raw`^(${num})\s*[,;]\s*(${num})$`),
    // Virgüllü ondalık: "41,0082 28,9784" veya "41,0082; 28,9784".
    /^(-?\d{1,3},\d+)\s*[;\s]\s*(-?\d{1,3},\d+)$/,
  ];
  for (const re of patterns) {
    const m = re.exec(s);
    if (m) return pick(m[1]!, m[2]!);
  }
  return null;
}

/** Ziyaretçiye mesafe metni için yuvarlama: 1 km altı 50 m adımla, üstü 0,1 km (10 km üstü tam km). */
export function roundDistance(m: number): { unit: 'm' | 'km'; value: number } {
  if (m < 1000) return { unit: 'm', value: Math.max(50, Math.round(m / 50) * 50) };
  if (m < 10_000) return { unit: 'km', value: Math.round(m / 100) / 10 };
  return { unit: 'km', value: Math.round(m / 1000) };
}
